import { PlanStoreyPolicy } from '@api/fastAPI.schemas'
import { isPlanStoreyElement } from '@editor/utils'

/**
 * What an element puts on a plan of one file format, as edited in the form. `showDetail` is the
 * second label line: the cross section of beams, purlins, columns, rips and lintels, the assembly
 * of ceilings and roof slabs.
 */
export interface PlanOutputValues {
  showGeometry: boolean
  showLabel: boolean
  showDetail: boolean
}

export interface PlanSettingsFormValues {
  planStoreyPolicy: PlanStoreyPolicy
  storeysForPlans: string[]
  use_global_live_loads?: boolean
  /** Not for walls, which are drawn on every plan. */
  pdf?: PlanOutputValues
  dxf?: PlanOutputValues
}

export const PLAN_STOREY_POLICY_OPTIONS: { value: PlanStoreyPolicy; label: string }[] = [
  { value: 'ElementStorey', label: 'Stockwerk des Elements' },
  { value: 'UserDefined', label: 'Benutzerdefiniert' },
]

const DEFAULT_SLAB_LIKE_OUTPUT: SlabLikePlanOutputSettings = {
  showGeometry: true,
  showLabel: true,
  showAssembly: true,
}

const DEFAULT_LINE_LIKE_OUTPUT: LineLikePlanOutputSettings = {
  showGeometry: true,
  showLabel: true,
  showCrossSection: true,
}

export const DEFAULT_SLAB_LIKE_SETTINGS = {
  positionPlanSettings: {
    planStoreyPolicy: 'ElementStorey' as PlanStoreyPolicy,
    storeysForPlans: [] as string[],
    pdf: DEFAULT_SLAB_LIKE_OUTPUT,
    dxf: DEFAULT_SLAB_LIKE_OUTPUT,
    labelPosition: null,
    labelOrientation: null,
  },
  certificateSettings: {} as SlabLikeCertificateSettings,
}

export const DEFAULT_LINE_LIKE_SETTINGS = {
  positionPlanSettings: {
    planStoreyPolicy: 'ElementStorey' as PlanStoreyPolicy,
    storeysForPlans: [] as string[],
    pdf: DEFAULT_LINE_LIKE_OUTPUT,
    dxf: DEFAULT_LINE_LIKE_OUTPUT,
    labelPosition: null,
    labelOrientation: null,
  },
  certificateSettings: {} as LineLikeCertificateSettings,
}

/** Plan settings to send when an element is (re-)created: its stored ones, or the defaults. */
export const lineLikeSettingsOf = (element?: {
  positionPlanSettings: LineLikePositionPlanSettings
  certificateSettings: LineLikeCertificateSettings
}) => ({
  positionPlanSettings:
    element?.positionPlanSettings ?? DEFAULT_LINE_LIKE_SETTINGS.positionPlanSettings,
  certificateSettings:
    element?.certificateSettings ?? DEFAULT_LINE_LIKE_SETTINGS.certificateSettings,
})

export const DEFAULT_WALL_SETTINGS = {
  positionPlanSettings: {
    planStoreyPolicy: 'ElementStorey' as PlanStoreyPolicy,
    storeysForPlans: [] as string[],
    showLabel: false,
    showAssembly: false,
    labelPosition: null,
    labelOrientation: null,
  },
  certificateSettings: {} as WallCertificateSettings,
}

export type WallSettings = Pick<Wall, 'positionPlanSettings' | 'certificateSettings'>

/** Plan settings to send when a wall is (re-)created: its stored ones, or the defaults. */
export const wallSettingsOf = (wall?: WallSettings): WallSettings => ({
  positionPlanSettings: wall?.positionPlanSettings ?? DEFAULT_WALL_SETTINGS.positionPlanSettings,
  certificateSettings: wall?.certificateSettings ?? DEFAULT_WALL_SETTINGS.certificateSettings,
})

/**
 * Elements whose plan storeys can be set: ceiling/roof positions, beams, purlins, columns, rips,
 * lintels and walls.
 */
export type PlanSettingsElement =
  | VerticalSlab
  | VerticalRoofSlab
  | PlanLineElement
  | Position
  | Wall

/** Rips and lintels live in the wall positions and are identified by their `position_guid`. */
export const isWallPosition = (element: PlanSettingsElement): element is Position =>
  element.element_type === 'rips' || element.element_type === 'lintels'

/** Ceilings and roof slabs: their second label line is the assembly. */
export const isSlabLike = (
  element: PlanSettingsElement,
): element is VerticalSlab | VerticalRoofSlab =>
  element.element_type === 'vertical_slabs' || element.element_type === 'vertical_roof_slabs'

/** Elements with stored plan settings, i.e. those the settings form edits. */
export const isPlanSettingsElement = (element: object): element is PlanSettingsElement =>
  isPlanStoreyElement(element)

/** The model store types walls by their placement. */
export const isWall = (element: PlanSettingsElement): element is Wall =>
  element.element_type === 'inner_walls' || element.element_type === 'outer_walls'

export const planSettingsElementId = (element: PlanSettingsElement): string =>
  isWallPosition(element) ? element.position_guid : element.guid

const outputValuesOf = (
  output: LineLikePlanOutputSettings | SlabLikePlanOutputSettings,
): PlanOutputValues => ({
  showGeometry: output.showGeometry,
  showLabel: output.showLabel,
  showDetail: 'showCrossSection' in output ? output.showCrossSection : output.showAssembly,
})

const planOutputValuesOf = (
  element: PlanSettingsElement,
): Pick<PlanSettingsFormValues, 'pdf' | 'dxf'> =>
  isWall(element)
    ? {}
    : {
        pdf: outputValuesOf(element.positionPlanSettings.pdf),
        dxf: outputValuesOf(element.positionPlanSettings.dxf),
      }

export const defaultPlanSettingsValues = (
  element: PlanSettingsElement,
): PlanSettingsFormValues => ({
  planStoreyPolicy: element.positionPlanSettings.planStoreyPolicy,
  storeysForPlans: element.positionPlanSettings.storeysForPlans.map(storey => String(storey)),
  ...planOutputValuesOf(element),
  ...(element.element_type === 'vertical_slabs'
    ? { use_global_live_loads: element.metadata?.use_global_live_loads ?? true }
    : {}),
})

const lineLikeOutputOf = ({
  showGeometry,
  showLabel,
  showDetail,
}: PlanOutputValues): LineLikePlanOutputSettings => ({
  showGeometry,
  showLabel,
  showCrossSection: showDetail,
})

const slabLikeOutputOf = ({
  showGeometry,
  showLabel,
  showDetail,
}: PlanOutputValues): SlabLikePlanOutputSettings => ({
  showGeometry,
  showLabel,
  showAssembly: showDetail,
})

/** `settings` with the PDF and DXF output edited in the form. */
export const lineLikeSettingsWithOutput = (
  settings: LineLikePositionPlanSettings,
  values: PlanSettingsFormValues,
): LineLikePositionPlanSettings => ({
  ...settings,
  ...(values.pdf && { pdf: lineLikeOutputOf(values.pdf) }),
  ...(values.dxf && { dxf: lineLikeOutputOf(values.dxf) }),
})

/** `settings` with the PDF and DXF output edited in the form. */
export const slabLikeSettingsWithOutput = (
  settings: SlabLikePositionPlanSettings,
  values: PlanSettingsFormValues,
): SlabLikePositionPlanSettings => ({
  ...settings,
  ...(values.pdf && { pdf: slabLikeOutputOf(values.pdf) }),
  ...(values.dxf && { dxf: slabLikeOutputOf(values.dxf) }),
})

export const storeysForPlansToPersist = (values: PlanSettingsFormValues): string[] =>
  values.planStoreyPolicy === 'UserDefined' ? values.storeysForPlans : []
